// src/components/SpaceDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SpaceProfile from "./SpaceProfile";

const API_BASE = "https://sy47l7fjif.execute-api.us-east-1.amazonaws.com/Prod";

const Badge = ({ children, kind = "default" }) => {
  const base =
    "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap";
  const styles = {
    default: "bg-gray-100 text-gray-700 border-gray-200",
    green: "bg-green-100 text-green-700 border-green-200",
    yellow: "bg-yellow-100 text-yellow-700 border-yellow-200",
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    red: "bg-red-100 text-red-700 border-red-200",
  };

  return (
    <span className={`${base} ${styles[kind] || styles.default}`}>
      {children}
    </span>
  );
};

const Button = ({
  children,
  onClick,
  disabled,
  variant = "primary",
  title,
}) => {
  const base =
    "inline-flex items-center justify-center px-4 py-2 rounded-xl text-sm font-medium transition border";
  const styles = {
    primary:
      "bg-yellow-500 text-white border-yellow-500 hover:bg-yellow-600 disabled:bg-gray-300 disabled:border-gray-300",
    secondary:
      "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 disabled:opacity-60",
    success:
      "bg-green-600 text-white border-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:border-gray-300",
  };

  return (
    <button
      className={`${base} ${styles[variant] || styles.primary}`}
      onClick={onClick}
      disabled={disabled}
      title={title}
      type="button"
    >
      {children}
    </button>
  );
};

const TabButton = ({ active, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-5 py-3 rounded-xl text-sm font-medium border transition ${
      active
        ? "bg-yellow-500 text-white border-yellow-500"
        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
    }`}
  >
    {children}
  </button>
);

const StatCard = ({ title, value, hint, kind = "default" }) => {
  const accents = {
    default: "from-gray-50 to-white border-gray-200",
    green: "from-green-50 to-white border-green-200",
    blue: "from-blue-50 to-white border-blue-200",
    yellow: "from-yellow-50 to-white border-yellow-200",
  };

  return (
    <div
      className={`rounded-3xl border bg-gradient-to-br ${
        accents[kind] || accents.default
      } p-6 shadow-sm`}
    >
      <div className="text-sm text-gray-500">{title}</div>
      <div className="mt-3 text-3xl font-bold text-gray-900 break-words">
        {value}
      </div>
      {hint ? <div className="mt-2 text-sm text-gray-500">{hint}</div> : null}
    </div>
  );
};

const SectionCard = ({ title, subtitle, children, action }) => (
  <div className="bg-white rounded-3xl border border-gray-200 shadow-sm">
    <div className="p-6 border-b border-gray-100 flex items-start justify-between gap-4 flex-wrap">
      <div>
        <div className="text-2xl font-bold text-gray-900">{title}</div>
        {subtitle ? <div className="mt-1 text-sm text-gray-500">{subtitle}</div> : null}
      </div>
      {action ? <div>{action}</div> : null}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const AccordionCard = ({
  title,
  subtitle,
  open,
  onToggle,
  summary,
  children,
  action,
}) => (
  <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
    <div className="p-6 flex items-start justify-between gap-4 flex-wrap">
      <div className="min-w-0">
        <div className="text-2xl font-bold text-gray-900">{title}</div>
        {subtitle ? <div className="mt-1 text-sm text-gray-500">{subtitle}</div> : null}
        {summary ? <div className="mt-3">{summary}</div> : null}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        {action ? action : null}
        <Button variant="secondary" onClick={onToggle}>
          {open ? "Weniger anzeigen" : "Mehr anzeigen"}
        </Button>
      </div>
    </div>

    {open ? (
      <div className="px-6 pb-6 border-t border-gray-100">{children}</div>
    ) : null}
  </div>
);

const InfoField = ({ label, value }) => (
  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
    <div className="text-sm text-gray-500">{label}</div>
    <div className="mt-2 text-lg font-medium text-gray-900 break-words">
      {value || "–"}
    </div>
  </div>
);

const formatCurrency = (amount, currency = "EUR") => {
  const numeric = Number(amount || 0);
  try {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency || "EUR",
    }).format(numeric);
  } catch {
    return `${numeric.toFixed(2)} ${currency || "EUR"}`;
  }
};

const formatDateTime = (iso) => {
  if (!iso) return "–";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString("de-DE");
  } catch {
    return String(iso);
  }
};

const safeStr = (v) =>
  v === null || v === undefined || v === "" ? "–" : String(v);

const safeNum = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const getCompanyId = () => {
  const direct = localStorage.getItem("company_id");
  if (direct) return direct;

  try {
    const ctxRaw = localStorage.getItem("company_context");
    if (!ctxRaw) return "";
    const ctx = JSON.parse(ctxRaw);
    return ctx.company_id || "";
  } catch {
    return "";
  }
};

const getCompanyName = () => {
  const direct = localStorage.getItem("company_name");
  if (direct) return direct;

  try {
    const ctxRaw = localStorage.getItem("company_context");
    if (!ctxRaw) return "";
    const ctx = JSON.parse(ctxRaw);
    return ctx.legal_name || ctx.company_name || "";
  } catch {
    return "";
  }
};

const getQontoInvoiceId = (inv) => inv?.external_invoice_id || inv?.id || "";
const getQontoAmount = (inv) => safeNum(inv?.amount_total);

export default function SpaceDashboard() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("inkasso");
  const [err, setErr] = useState("");

  const [profileLoading, setProfileLoading] = useState(true);
  const [spaceProfile, setSpaceProfile] = useState(null);

  const [loadingSpaces, setLoadingSpaces] = useState(false);
  const [spaces, setSpaces] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState("");
  const [selectedSpaceName, setSelectedSpaceName] = useState("");

  const [providerOpen, setProviderOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const [loadingCases, setLoadingCases] = useState(false);
  const [handoffByInvoiceId, setHandoffByInvoiceId] = useState({});

  const [qontoLoading, setQontoLoading] = useState(false);
  const [qontoInvoices, setQontoInvoices] = useState([]);

  const token =
    localStorage.getItem("space_token") || localStorage.getItem("token") || "";

  const spaceIdFromStorage =
    localStorage.getItem("space_id") || localStorage.getItem("SpaceID") || "";

  const companyId = getCompanyId();
  const companyName = getCompanyName();

  const logout = () => {
    localStorage.removeItem("space_token");
    localStorage.removeItem("space_id");
    localStorage.removeItem("SpaceID");
    localStorage.removeItem("token");
    localStorage.removeItem("selected_space_subdomain");
    window.location.href = "/";
  };

  const loadSpaces = async () => {
    setLoadingSpaces(true);
    try {
      setErr("");

      if (!spaceIdFromStorage) {
        setSpaces([]);
        setSelectedSpace("");
        setSelectedSpaceName("");
        return;
      }

      const res = await fetch(
        `${API_BASE}/Cobot/Spaces?space_id=${encodeURIComponent(
          spaceIdFromStorage
        )}`,
        { method: "GET" }
      );

      const txt = await res.text();
      if (!res.ok) {
        throw new Error(`GET /Cobot/Spaces failed: ${res.status} ${txt}`);
      }

      const data = txt ? JSON.parse(txt) : {};
      const list = Array.isArray(data?.spaces) ? data.spaces : [];

      setSpaces(list);

      const storedSelected =
        localStorage.getItem("selected_space_subdomain") ||
        localStorage.getItem("space_id") ||
        "";

      const fallback = list?.[0]?.subdomain || "";
      const nextSelected =
        list.find((s) => s?.subdomain === storedSelected)?.subdomain ||
        (storedSelected && !list.length ? storedSelected : "") ||
        fallback;

      setSelectedSpace(nextSelected);

      const nm =
        list.find((s) => s?.subdomain === nextSelected)?.name ||
        nextSelected ||
        "";
      setSelectedSpaceName(nm);
    } catch (e) {
      setSpaces([]);
      setSelectedSpace("");
      setSelectedSpaceName("");
      setErr(e?.message || String(e));
    } finally {
      setLoadingSpaces(false);
    }
  };

  const loadSpaceProfile = async (spaceSubdomain) => {
    setProfileLoading(true);
    try {
      if (!spaceSubdomain) {
        setSpaceProfile(null);
        return;
      }

      const res = await fetch(
        `${API_BASE}/Cobot/Space?space_id=${encodeURIComponent(spaceSubdomain)}`,
        { method: "GET" }
      );

      const txt = await res.text();
      if (!res.ok) {
        throw new Error(`GET /Cobot/Space failed: ${res.status} ${txt}`);
      }

      const data = txt ? JSON.parse(txt) : {};
      setSpaceProfile(data?.space || null);
    } catch (e) {
      setSpaceProfile(null);
      setErr((prev) => prev || e?.message || String(e));
    } finally {
      setProfileLoading(false);
    }
  };

  const loadInkassoCases = async (spaceSubdomain) => {
    setLoadingCases(true);
    try {
      if (!spaceSubdomain) {
        setHandoffByInvoiceId({});
        return;
      }

      const res = await fetch(
        `${API_BASE}/inkasso?space_id=${encodeURIComponent(spaceSubdomain)}`,
        { method: "GET" }
      );

      const txt = await res.text();
      if (!res.ok) {
        throw new Error(`GET /inkasso failed: ${res.status} ${txt}`);
      }

      const data = txt ? JSON.parse(txt) : [];
      const list = Array.isArray(data)
        ? data
        : data?.handoffs || data?.items || [];

      const map = {};
      for (const c of list) {
        const invoiceId = c?.invoice_id || c?.id;
        if (invoiceId) map[String(invoiceId)] = c;
      }
      setHandoffByInvoiceId(map);
    } catch (e) {
      setErr((prev) => prev || e?.message || String(e));
      setHandoffByInvoiceId({});
    } finally {
      setLoadingCases(false);
    }
  };

  const loadQontoInvoices = async () => {
    setQontoLoading(true);
    try {
      setErr("");

      const companyIdForCall = getCompanyId();
      if (!companyIdForCall) {
        setQontoInvoices([]);
        return;
      }

      const url = `${API_BASE}/qonto?company_id=${encodeURIComponent(
        companyIdForCall
      )}&mode=inkasso_ready&only_unpaid=true&exclude_cancelled=true&only_overdue=true`;

      const res = await fetch(url, { method: "GET" });
      const txt = await res.text();

      if (!res.ok) {
        throw new Error(`GET /qonto failed: ${res.status} ${txt}`);
      }

      const data = txt ? JSON.parse(txt) : {};
      const list = Array.isArray(data?.cases_preview)
        ? data.cases_preview
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
        ? data
        : [];

      setQontoInvoices(list);
    } catch (e) {
      setQontoInvoices([]);
      setErr(e?.message || String(e));
    } finally {
      setQontoLoading(false);
    }
  };

  const refreshAll = async () => {
    setErr("");
    await Promise.all([
      loadSpaces(),
      selectedSpace ? loadInkassoCases(selectedSpace) : Promise.resolve(),
      selectedSpace ? loadSpaceProfile(selectedSpace) : Promise.resolve(),
      loadQontoInvoices(),
    ]);
  };

  useEffect(() => {
    loadSpaces();
    loadQontoInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedSpace) return;
    loadInkassoCases(selectedSpace);
    loadSpaceProfile(selectedSpace);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpace]);

  const providerSummary = useMemo(() => {
    const cobotConnected = !!selectedSpace;
    const qontoConnected = qontoInvoices.length > 0;

    return {
      total: [cobotConnected, qontoConnected].filter(Boolean).length,
      cobotConnected,
      qontoConnected,
    };
  }, [selectedSpace, qontoInvoices.length]);

  const inkassoStats = useMemo(() => {
    const cases = Object.values(handoffByInvoiceId || {});
    const totalCases = cases.length;

    const totalOpenAmount = cases.reduce((sum, item) => {
      const amount =
        Number(
          item?.offene_hf ??
            item?.amount ??
            item?.principal_amount ??
            item?.claim_amount ??
            0
        ) || 0;
      return sum + amount;
    }, 0);

    const totalOpenCosts = cases.reduce((sum, item) => {
      const amount =
        Number(item?.kosten ?? item?.uebern_kosten ?? item?.costs ?? 0) || 0;
      return sum + amount;
    }, 0);

    const totalOverall = totalOpenAmount + totalOpenCosts;

    let lastUpdated = null;
    cases.forEach((item) => {
      const candidate = item?.updated_at || item?.submitted_at || item?.created_at;
      if (!candidate) return;

      const ts = new Date(candidate).getTime();
      if (Number.isNaN(ts)) return;

      if (!lastUpdated || ts > new Date(lastUpdated).getTime()) {
        lastUpdated = candidate;
      }
    });

    return {
      totalCases,
      totalOpenAmount,
      totalOpenCosts,
      totalOverall,
      lastUpdated,
    };
  }, [handoffByInvoiceId]);

  const showSpaceProfileBlocking =
    !profileLoading &&
    selectedSpace &&
    (!spaceProfile || spaceProfile.inkasso_agb_accepted !== true);

  if (profileLoading && selectedSpace) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-[1320px] mx-auto px-8 py-5">
            <div className="text-xl font-semibold text-gray-900">
              Dashboard
            </div>
            <div className="text-sm text-gray-500">Lade Profil…</div>
          </div>
        </div>
      </div>
    );
  }

  if (showSpaceProfileBlocking) {
    return (
      <SpaceProfile
        spaceId={selectedSpace || spaceIdFromStorage}
        onSaved={(p) => setSpaceProfile(p)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-[1320px] mx-auto px-8 py-5 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
            <div className="text-sm text-gray-500 truncate">
              {companyName || selectedSpaceName || "Mandantenbereich"}
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button variant="secondary" onClick={refreshAll}>
              Alles neu laden
            </Button>
            <Button variant="secondary" onClick={logout}>
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1320px] mx-auto px-8 py-10 space-y-8">
        {err ? (
          <div className="rounded-2xl bg-red-50 border border-red-200 shadow-sm p-5">
            <div className="text-red-800 font-semibold">Fehler</div>
            <div className="mt-1 text-sm text-red-700">{err}</div>
          </div>
        ) : null}

        <SectionCard
          title={`Willkommen${companyName ? `, ${companyName}` : ""}`}
          subtitle="Zentrale Übersicht über Verbindungen, Profil und Inkasso-Bereich."
        >
          <div className="flex gap-2 flex-wrap">
            <Badge kind={providerSummary.cobotConnected ? "green" : "default"}>
              {providerSummary.cobotConnected
                ? "Cobot verbunden"
                : "Cobot nicht verbunden"}
            </Badge>
            <Badge kind={providerSummary.qontoConnected ? "green" : "default"}>
              {providerSummary.qontoConnected
                ? "Qonto verbunden"
                : "Qonto nicht verbunden"}
            </Badge>
            <Badge
              kind={
                spaceProfile?.inkasso_agb_accepted === true ? "green" : "yellow"
              }
            >
              {spaceProfile?.inkasso_agb_accepted === true
                ? "Firmenprofil vollständig"
                : "Firmenprofil prüfen"}
            </Badge>
          </div>
        </SectionCard>

        <SectionCard
          title="Aktuelle Provider-Verbindungen"
          subtitle="Überblick über die derzeit aktiven Anbindungen für diesen Mandanten."
          action={
            <Button
              variant="secondary"
              onClick={refreshAll}
              disabled={loadingSpaces || qontoLoading || loadingCases}
            >
              Neu laden
            </Button>
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            <StatCard
              title="Verbundene Provider"
              value={providerSummary.total}
              hint="Aktuell erkannte aktive Anbindungen"
              kind="yellow"
            />
            <StatCard
              title="Cobot"
              value={
                providerSummary.cobotConnected ? "Verbunden" : "Nicht verbunden"
              }
              hint={selectedSpaceName || "Keine aktive Cobot-Verbindung"}
              kind={providerSummary.cobotConnected ? "green" : "default"}
            />
            <StatCard
              title="Qonto"
              value={
                providerSummary.qontoConnected ? "Verbunden" : "Nicht verbunden"
              }
              hint={
                providerSummary.qontoConnected
                  ? `${qontoInvoices.length} erkennbare Rechnungen`
                  : "Keine aktive Qonto-Verbindung"
              }
              kind={providerSummary.qontoConnected ? "blue" : "default"}
            />
            <StatCard
              title="Firmenprofil"
              value={spaceProfile?.inkasso_agb_accepted ? "OK" : "Offen"}
              hint={
                spaceProfile?.inkasso_agb_accepted
                  ? "AGB akzeptiert und Profil vorhanden"
                  : "Bitte Profil prüfen"
              }
              kind={spaceProfile?.inkasso_agb_accepted ? "green" : "yellow"}
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Provider verbinden"
          subtitle="Weitere Datenquellen für diesen Mandanten ergänzen."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="rounded-3xl border border-gray-200 bg-white p-6">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-xl font-semibold text-gray-900">Cobot</div>
                  <div className="mt-1 text-sm text-gray-500">
                    Coworking / Rechnungen / Mahnstufen
                  </div>
                </div>
                <Badge
                  kind={providerSummary.cobotConnected ? "green" : "default"}
                >
                  {providerSummary.cobotConnected ? "Verbunden" : "Verfügbar"}
                </Badge>
              </div>

              <div className="mt-5">
                <Button
                  variant={
                    providerSummary.cobotConnected ? "secondary" : "primary"
                  }
                  onClick={() => navigate("/space-profile")}
                >
                  {providerSummary.cobotConnected
                    ? "Verbindung prüfen"
                    : "Cobot verbinden"}
                </Button>
              </div>
            </div>

            <div className="rounded-3xl border border-gray-200 bg-white p-6">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-xl font-semibold text-gray-900">Qonto</div>
                  <div className="mt-1 text-sm text-gray-500">
                    Rechnungen für Inkasso-Übergaben
                  </div>
                </div>
                <Badge
                  kind={providerSummary.qontoConnected ? "green" : "default"}
                >
                  {providerSummary.qontoConnected ? "Verbunden" : "Verfügbar"}
                </Badge>
              </div>

              <div className="mt-5">
                <Button
                  variant={
                    providerSummary.qontoConnected ? "secondary" : "primary"
                  }
                  onClick={() => navigate("/company-dashboard")}
                >
                  {providerSummary.qontoConnected
                    ? "Verbindung prüfen"
                    : "Qonto verbinden"}
                </Button>
              </div>
            </div>
          </div>
        </SectionCard>

        <AccordionCard
          title="Firmenprofil"
          subtitle="Gespeicherte Mandanten- und Profildaten."
          open={profileOpen}
          onToggle={() => setProfileOpen((prev) => !prev)}
          action={
            <Button
              variant="secondary"
              onClick={() => navigate("/space-profile")}
            >
              Firmenprofil öffnen
            </Button>
          }
          summary={
            <div className="flex gap-2 flex-wrap">
              <Badge kind="default">
                {safeStr(spaceProfile?.legal_name || companyName)}
              </Badge>
              <Badge kind="default">{safeStr(companyId)}</Badge>
              <Badge
                kind={spaceProfile?.inkasso_agb_accepted ? "green" : "yellow"}
              >
                {spaceProfile?.inkasso_agb_accepted
                  ? "AGB akzeptiert"
                  : "AGB offen"}
              </Badge>
            </div>
          }
        >
          <div className="pt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            <InfoField
              label="Rechtlicher Name"
              value={spaceProfile?.legal_name || companyName || "–"}
            />
            <InfoField label="Company ID" value={companyId || "–"} />
            <InfoField label="Space ID" value={selectedSpace || "–"} />
            <InfoField
              label="E-Mail"
              value={spaceProfile?.contact_email || "–"}
            />
            <InfoField
              label="Telefon"
              value={spaceProfile?.contact_phone || "–"}
            />
            <InfoField label="IBAN" value={spaceProfile?.iban || "–"} />
            <div className="md:col-span-2">
              <InfoField
                label="Adresse"
                value={[
                  spaceProfile?.address_street,
                  spaceProfile?.address_zip,
                  spaceProfile?.address_city,
                  spaceProfile?.address_country,
                ]
                  .filter(Boolean)
                  .join(", ")}
              />
            </div>
            <InfoField
              label="AGB Status"
              value={spaceProfile?.inkasso_agb_accepted ? "akzeptiert" : "offen"}
            />
          </div>
        </AccordionCard>

        <AccordionCard
          title="Provider"
          subtitle="Details zu aktiven oder erkannten Verbindungen."
          open={providerOpen}
          onToggle={() => setProviderOpen((prev) => !prev)}
          summary={
            <div className="flex gap-2 flex-wrap">
              <Badge kind={providerSummary.cobotConnected ? "green" : "default"}>
                Cobot{" "}
                {providerSummary.cobotConnected
                  ? "verbunden"
                  : "nicht verbunden"}
              </Badge>
              <Badge kind={providerSummary.qontoConnected ? "green" : "default"}>
                Qonto{" "}
                {providerSummary.qontoConnected
                  ? "verbunden"
                  : "nicht verbunden"}
              </Badge>
            </div>
          }
        >
          <div className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="rounded-3xl border border-gray-200 bg-gray-50 p-6">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge kind={providerSummary.cobotConnected ? "green" : "default"}>
                  Cobot
                </Badge>
                <Badge kind={providerSummary.cobotConnected ? "green" : "default"}>
                  {providerSummary.cobotConnected
                    ? "verbunden"
                    : "nicht verbunden"}
                </Badge>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-4">
                <InfoField label="Provider" value="cobot" />
                <InfoField
                  label="Status"
                  value={
                    providerSummary.cobotConnected
                      ? "connected"
                      : "not connected"
                  }
                />
                <InfoField label="External ID" value={selectedSpace || "–"} />
                <InfoField
                  label="Referenz"
                  value={
                    providerSummary.cobotConnected
                      ? "stored_in_cobot_tokens"
                      : "–"
                  }
                />
              </div>
            </div>

            <div className="rounded-3xl border border-gray-200 bg-gray-50 p-6">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge kind={providerSummary.qontoConnected ? "green" : "default"}>
                  Qonto
                </Badge>
                <Badge kind={providerSummary.qontoConnected ? "green" : "default"}>
                  {providerSummary.qontoConnected
                    ? "verbunden"
                    : "nicht verbunden"}
                </Badge>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-4">
                <InfoField label="Provider" value="qonto" />
                <InfoField
                  label="Status"
                  value={
                    providerSummary.qontoConnected
                      ? "connected"
                      : "not connected"
                  }
                />
                <InfoField
                  label="Erkannte Rechnungen"
                  value={
                    providerSummary.qontoConnected
                      ? String(qontoInvoices.length)
                      : "0"
                  }
                />
                <InfoField
                  label="Referenz"
                  value={providerSummary.qontoConnected ? "qonto-api" : "–"}
                />
              </div>
            </div>
          </div>
        </AccordionCard>

        <SectionCard
          title="Bereiche"
          subtitle="Navigation für die nächsten Ausbaustufen des Dashboards."
        >
          <div className="flex items-center gap-3 flex-wrap">
            <TabButton
              active={activeTab === "overview"}
              onClick={() => setActiveTab("overview")}
            >
              Übersicht
            </TabButton>
            <TabButton
              active={activeTab === "cobot"}
              onClick={() => setActiveTab("cobot")}
            >
              Cobot
            </TabButton>
            <TabButton
              active={activeTab === "qonto"}
              onClick={() => setActiveTab("qonto")}
            >
              Qonto
            </TabButton>
            <TabButton
              active={activeTab === "inkasso"}
              onClick={() => setActiveTab("inkasso")}
            >
              Inkasso Cases
            </TabButton>
          </div>
        </SectionCard>

        {activeTab === "inkasso" && (
          <SectionCard
            title="Inkasso Übersicht"
            subtitle="Zentrale Vorschau auf den aktuellen Stand der offenen Akten."
            action={
              <div className="flex gap-3 flex-wrap">
                <Badge kind="yellow">Mandantensicht</Badge>
                <Button
                  variant="primary"
                  onClick={() => navigate("/mandant-cases")}
                >
                  Zur Aktenübersicht
                </Button>
              </div>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-8">
              <StatCard
                title="Offene Akten"
                value={inkassoStats.totalCases}
                hint="Aktueller offener Bestand"
                kind="yellow"
              />
              <StatCard
                title="Offene Hauptforderung"
                value={formatCurrency(inkassoStats.totalOpenAmount, "EUR")}
                hint="Summierter offener Betrag"
                kind="green"
              />
              <StatCard
                title="Offene Kosten"
                value={formatCurrency(inkassoStats.totalOpenCosts, "EUR")}
                hint="Summierte offene Kosten"
                kind="blue"
              />
              <StatCard
                title="Gesamt offen"
                value={formatCurrency(inkassoStats.totalOverall, "EUR")}
                hint="Summe aus importierten Gesamtwerten"
                kind="yellow"
              />
            </div>

            <div className="grid grid-cols-1">
              <StatCard
                title="Letzte Aktualisierung"
                value={formatDateTime(inkassoStats.lastUpdated)}
                hint="Zeitpunkt der letzten erkannten Änderung"
                kind="blue"
              />
            </div>
          </SectionCard>
        )}

        {activeTab === "overview" && (
          <SectionCard
            title="Übersicht"
            subtitle="Kompakte Startübersicht des Mandantenbereichs."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
              <StatCard
                title="Company ID"
                value={companyId || "–"}
                hint="Zentrale Mandanten-ID"
                kind="default"
              />
              <StatCard
                title="Cobot"
                value={providerSummary.cobotConnected ? "Aktiv" : "Inaktiv"}
                hint={selectedSpaceName || "Kein Space aktiv"}
                kind={providerSummary.cobotConnected ? "green" : "default"}
              />
              <StatCard
                title="Qonto"
                value={providerSummary.qontoConnected ? "Aktiv" : "Inaktiv"}
                hint={
                  providerSummary.qontoConnected
                    ? `${qontoInvoices.length} Rechnungen erkannt`
                    : "Keine aktive Verbindung"
                }
                kind={providerSummary.qontoConnected ? "blue" : "default"}
              />
              <StatCard
                title="Inkasso"
                value={inkassoStats.totalCases}
                hint="Offene Akten in der Übersicht"
                kind="yellow"
              />
            </div>
          </SectionCard>
        )}

        {activeTab === "cobot" && (
          <SectionCard
            title="Cobot"
            subtitle="Kompakte Statusübersicht zur Cobot-Anbindung."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
              <StatCard
                title="Status"
                value={
                  providerSummary.cobotConnected ? "Verbunden" : "Nicht verbunden"
                }
                hint="Cobot-Verbindung auf Mandantenebene"
                kind={providerSummary.cobotConnected ? "green" : "default"}
              />
              <StatCard
                title="Space"
                value={selectedSpaceName || "–"}
                hint={selectedSpace || "Kein Space ausgewählt"}
                kind="blue"
              />
              <StatCard
                title="Token"
                value={token ? "OK" : "Fehlt"}
                hint="Cobot-Zugriffstoken"
                kind={token ? "green" : "yellow"}
              />
              <StatCard
                title="Profilstatus"
                value={spaceProfile?.inkasso_agb_accepted ? "OK" : "Offen"}
                hint="Profil / AGB"
                kind={spaceProfile?.inkasso_agb_accepted ? "green" : "yellow"}
              />
            </div>
          </SectionCard>
        )}

        {activeTab === "qonto" && (
          <SectionCard
            title="Qonto"
            subtitle="Kompakte Statusübersicht zur Qonto-Anbindung."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
              <StatCard
                title="Status"
                value={
                  providerSummary.qontoConnected ? "Verbunden" : "Nicht verbunden"
                }
                hint="Qonto-Anbindung auf Mandantenebene"
                kind={providerSummary.qontoConnected ? "green" : "default"}
              />
              <StatCard
                title="Rechnungen erkannt"
                value={qontoInvoices.length}
                hint="Aktuell erkennbare Rechnungen"
                kind="blue"
              />
              <StatCard
                title="Gesamtbetrag"
                value={formatCurrency(
                  qontoInvoices.reduce(
                    (sum, inv) => sum + getQontoAmount(inv),
                    0
                  ),
                  "EUR"
                )}
                hint="Summiert aus erkannter Vorschau"
                kind="green"
              />
              <StatCard
                title="Company ID"
                value={companyId || "–"}
                hint="Mandantenbezug für Qonto"
                kind="default"
              />
            </div>
          </SectionCard>
        )}
      </div>
    </div>
  );
}